<script setup lang="ts">
import BaseButton from '@/components/BaseButton.vue'
import BaseInput from '@/components/BaseInput.vue'
import BaseSelect from '@/components/BaseSelect.vue'
import { session, supabase } from '@/composables/supabaseClient'
import toastThrowError from '@/helpers/toastThrowError'
import { tryCatchAsync } from '@/helpers/tryCatch'
import uuidv7 from '@/helpers/uuidv7'
import router from '@/router'
import { useDatabaseStore } from '@/stores/databaseStore'
import { useEstimateStore } from '@/stores/estimateStore'
import type { Tables } from '@/types/database.types'
import { computed, ref, watch } from 'vue'

const databaseStore = useDatabaseStore()
const estimateStore = useEstimateStore()

const newEstimate = ref<{
  code: string | null
  name: string | null
  estimate_type_id: string | null
  estimate_status_id: string | null
  cost_source_id: string | null
}>({
  code: null,
  name: null,
  estimate_type_id: null,
  estimate_status_id: null,
  cost_source_id: databaseStore.costSources?.find((c) => c.name === 'Default')?.id ?? null,
})
const estimateTemplates = computed(() => databaseStore.estimates?.filter((e) => e.is_template))

const selectedTemplateId = ref<Tables<'estimate'>['id']>()
async function addEstimate() {
  Object.entries(newEstimate.value).forEach((entry) => {
    const name = entry[0]
    const value = entry[1]
    if (name !== 'is_template') {
      if (value === undefined || value === null || value === '') {
        throw toastThrowError(`Missing required field: ${name}`)
      }
    }
  })
  const estimateResult = await supabase
    .from('estimate')
    .insert({
      id: uuidv7(),
      code: newEstimate.value.code!,
      name: newEstimate.value.name!,
      estimate_type_id: newEstimate.value.estimate_type_id!,
      estimate_status_id: newEstimate.value.estimate_status_id!,
      cost_source_id: newEstimate.value.cost_source_id!,
      is_template: false,
      owner_id: session.value!.user.id,
    })
    .select()
    .single()
  if (estimateResult.error) throw toastThrowError('Unable to add estimate', estimateResult.error)
  await estimateStore.loadEstimate(estimateResult.data.id)
  if (selectedTemplateId.value !== undefined) {
    // Add items from template
    const itemAssemblyResult = await tryCatchAsync(async () => {
      const assembliesToAdd = await supabase
        .from('estimate_assembly')
        .select()
        .eq('estimate_id', selectedTemplateId.value!)
      const itemsToAdd = await supabase
        .from('estimate_item')
        .select()
        .eq('estimate_id', selectedTemplateId.value!)
        .is('estimate_assembly_id', null)
      await Promise.all(
        assembliesToAdd.data?.map(async (assemblyToAdd) => {
          const variables = assemblyToAdd.variables as Record<string, number | string> | undefined
          await estimateStore.createEstimateItemsByAssembly(assemblyToAdd.id, variables ?? {})
        }) ?? [],
      )
      await Promise.all(
        itemsToAdd.data?.map(async (itemToAdd) => {
          await estimateStore.createEstimateItem(itemToAdd.id, null)
        }) ?? [],
      )
    })
    if (itemAssemblyResult.error)
      throw toastThrowError(
        'Unable to complete creating estimate from template. Check to see if estimate was created.',
      )
  }
  databaseStore.estimates?.push(estimateResult.data)
  await router.push({ name: 'Estimate', params: { estimateID: estimateResult.data.id } })
}

// TODO - Allow copying from a template
//  - When a template is selected, prefill everything but the estimate code and name from the template
//  - On save, create estimate items from the template
watch(selectedTemplateId, () => {
  const templateEstimate = databaseStore.estimates?.find((e) => e.id === selectedTemplateId.value)
  if (!templateEstimate) return
  newEstimate.value.estimate_type_id = templateEstimate.estimate_type_id
  newEstimate.value.cost_source_id = templateEstimate.cost_source_id
  newEstimate.value.estimate_status_id = templateEstimate.estimate_status_id
})
</script>

<template>
  <div class="flex h-full w-full flex-col gap-3">
    <header class="bg-blue-300 p-4 text-xl font-bold"><h1>Create Estimate</h1></header>
    <form class="flex flex-col gap-3 p-3">
      <BaseSelect
        v-if="estimateTemplates && estimateTemplates.length > 0"
        allow-blank
        name="Use Template"
        v-model="selectedTemplateId"
        :select-options="estimateTemplates"
        option-key="id"
        option-name="name"
        option-value="id"
      />
      <BaseInput name="Code" v-model="newEstimate.code" required />
      <BaseInput name="Name" v-model="newEstimate.name" required />
      <BaseSelect
        allow-blank
        name="Type"
        v-model="newEstimate.estimate_type_id"
        :select-options="databaseStore.estimateTypes"
        option-key="id"
        option-name="name"
        option-value="id"
      />
      <BaseSelect
        allow-blank
        name="Status"
        v-model="newEstimate.estimate_status_id"
        :select-options="databaseStore.estimateStatus"
        option-key="id"
        option-name="name"
        option-value="id"
      />
      <BaseSelect
        allow-blank
        name="Cost Source"
        v-model="newEstimate.cost_source_id"
        :select-options="databaseStore.costSources"
        option-key="id"
        option-name="name"
        option-value="id"
      />
      <BaseButton @click="addEstimate()">Save</BaseButton>
    </form>
  </div>
</template>
